// KataTafel.jsx — Formen-Leinwand im Turniertag-Bild (20.10.2026)
//
// Vorlage: docs/muster-animationen/muster.html, Seite „Kata“, Ansicht A
// (vom Veranstalter abgenommen). Die Felder liegen in den Rahmen von
// /turniertag/tafel_gleich.jpg; die Lage ist im Muster in Prozent des
// 1672x941-Bildes vermessen und hier übernommen.
//
// Daten: dasselbe Paket wie bisher (FormenSchreiberDashboard bzw.
// SVSchreiberPage über lib/leinwand.js). Gerechnet wird hier NICHTS:
// Gesamtwertung und Streichung kommen aus der API (formenScoring.js), die
// Tafel zeigt, welche Noten gestrichen sind (scoring.gestrichen =
// Kampfrichter-Nummern). Platz und Abstand sind nur Reihenfolge bzw.
// Unterschied der gelieferten Gesamtwertungen.
//
// Selbstverteidigung: dieselbe Tafel, Kacheln „Übung 1..5“, Noten erscheinen
// sofort, keine Streich-Anzeige (CLAUDE.md).
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './KataTafel.css';
import {
  TEMPO_KATA, animiere, warte, zahlFegt, Medaille, starterBild, bildUrl, punkteText, ohneBewegung,
} from './kataEhrungAnim';

const T = TEMPO_KATA;
const LEER = { id: null, n: 0, strich: false, summe: false, rang: false, platz: false };
const VOLL = { n: 99, strich: true, summe: true, rang: true, platz: true };
// Zeilen der Rangliste: so viele, wie über den Tiger passen (Muster:
// rangZeichnen rechnet mit 38 % Feldhöhe = 7 Zeilen; im Bildschirmfoto
// 1280x720 berührte Zeile 7 den Kopf des Tigers, darum 33 % = 6 Zeilen).
const MAX_ZEILEN = Math.max(3, 1 + Math.floor((33 - 7.5 - 1.4) / 4.6));

const hatWert = (w) => w && w.punkte !== null && w.punkte !== undefined && w.punkte !== '';
const name = (p) => [p?.vorname, p?.nachname].filter(Boolean).join(' ');

// halle = { turnierId, k } aus der Adresse der Leinwand: nur damit zeigt die
// Tafel Fotos (lib/hallenbild.js, 21.10.2026)
export default function KataTafel({ stand, matte, halle = null }) {
  const sv = stand?.disziplin === 'sv';
  const typ = stand?.typ || 'bereit';

  // Rangliste und Ergebnis kommen ohne Noten; dann bleibt der zuletzt
  // gezeigte Starter mit seinen Noten auf der Tafel stehen.
  const merk = useRef({ tn: null, noten: null, scoring: null, anzahl: 5 });
  let tn = stand?.teilnehmer || null;
  if (stand?.wertungen?.length) merk.current.anzahl = stand.wertungen.length;
  if (tn && typ === 'wertung') merk.current = { ...merk.current, tn, noten: stand.wertungen || [], scoring: stand.scoring || null };
  if (tn && tn.wettkaempfer_id !== merk.current.tn?.wettkaempfer_id && typ !== 'wertung') {
    merk.current = { ...merk.current, tn, noten: null, scoring: null };
  }
  if (!tn && typ === 'rangliste') tn = merk.current.tn;
  const tnId = tn?.wettkaempfer_id ?? null;
  const gleicherTn = tnId != null && merk.current.tn?.wettkaempfer_id === tnId;
  const noten = typ === 'wertung' ? (stand.wertungen || [])
    : (gleicherTn && merk.current.noten) ? merk.current.noten
      : Array.from({ length: merk.current.anzahl }, (_, i) => ({ kampfrichter_nummer: i + 1, punkte: null, eingegangen: false }));
  const scoring = (typ === 'wertung' || typ === 'ergebnis') ? (stand.scoring || null)
    : (typ === 'rangliste' && gleicherTn) ? merk.current.scoring : null;
  const vollstaendig = !!scoring?.vollstaendig && typ !== 'bereit';
  const gestrichen = sv ? [] : (scoring?.gestrichen || []);

  // ── Enthüllen (Formen): Noten fegen ein, Striche, Summe, Einsortieren ──
  const [enth, setEnth] = useState(LEER);
  const laufNr = useRef(0);
  const ersterStand = useRef(true);
  const buehneRef = useRef(null);
  const fxRef = useRef(null);
  const notenRefs = useRef([]);
  const strichRefs = useRef([]);
  const summeRef = useRef(null);
  const platzRef = useRef(null);
  const texte = useRef({});

  useEffect(() => {
    const zuEnde = () => setEnth({ id: tnId, ...VOLL });
    if (tnId == null) { laufNr.current += 1; setEnth(LEER); return; }
    if (sv) { laufNr.current += 1; zuEnde(); return; }
    if (!vollstaendig) {
      laufNr.current += 1;
      setEnth((e) => (e.id === tnId && !e.n ? e : { ...LEER, id: tnId }));
      return;
    }
    if (enth.id === tnId && enth.platz) return;           // schon enthüllt
    // Beim Öffnen der Seite oder ohne Bewegung: gleich der Endstand
    if (ersterStand.current || ohneBewegung()) { laufNr.current += 1; zuEnde(); return; }
    const lauf = ++laufNr.current;
    const weiter = () => lauf === laufNr.current;
    (async () => {
      setEnth({ ...LEER, id: tnId });
      await warte(300, T); if (!weiter()) return;
      const felder = notenRefs.current;
      for (let i = 0; i < (texte.current.noten || []).length; i++) {
        const w = felder[i];
        zahlFegt(buehneRef.current, fxRef.current, w, texte.current.noten[i], 2.2, T);
        await warte(380, T); if (!weiter()) return;
        setEnth((e) => ({ ...e, n: i + 1 }));
        animiere(w, [{ transform: 'scale(1.4)', color: '#fff3c4' }, { transform: 'scale(1)', color: '#fff' }], { duration: 500 }, T);
        await warte(320, T); if (!weiter()) return;
      }
      await warte(300, T); if (!weiter()) return;
      setEnth((e) => ({ ...e, strich: true }));
      await warte(700, T); if (!weiter()) return;
      zahlFegt(buehneRef.current, fxRef.current, summeRef.current, texte.current.summe, 1.4, T);
      await warte(380, T); if (!weiter()) return;
      setEnth((e) => ({ ...e, summe: true }));
      animiere(summeRef.current, [{ transform: 'scale(1.35)', color: '#fff3c4' }, { transform: 'scale(1)', color: '#fff' }], { duration: 650, easing: 'cubic-bezier(.2,.8,.2,1)' }, T);
      await warte(900, T); if (!weiter()) return;
      setEnth((e) => ({ ...e, rang: true }));
      await warte(60, T); if (!weiter()) return;
      zahlFegt(buehneRef.current, fxRef.current, platzRef.current, texte.current.platz, 1.6, T);
      await warte(380, T); if (!weiter()) return;
      setEnth((e) => ({ ...e, platz: true }));
      animiere(platzRef.current, [{ transform: 'scale(1.35)', color: '#fff3c4' }, { transform: 'scale(1)', color: '#fff' }], { duration: 650 }, T);
    })();
    // enth absichtlich nicht in den Abhängigkeiten: der Ablauf setzt es selbst
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tnId, vollstaendig, sv]);

  useEffect(() => { if (stand) ersterStand.current = false; }, [stand]);
  useEffect(() => () => { laufNr.current += 1; }, []);

  // Striche über die gestrichenen Noten
  useEffect(() => {
    strichRefs.current.forEach((s) => s && s.getAnimations().forEach((a) => a.cancel()));
    if (!enth.strich || ohneBewegung()) return;
    strichRefs.current.forEach((s) => {
      if (!s || !s.dataset.weg) return;
      animiere(s, [{ transform: 'rotate(-12deg) scaleX(0)' }, { transform: 'rotate(-12deg) scaleX(1)' }],
        { duration: 320, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'forwards' }, T);
    });
  }, [enth.strich, tnId]);

  // Selbstverteidigung: neu eingegangene Übung fegt ein
  const svZahl = useRef({ id: null, n: 0 });
  useEffect(() => {
    if (!sv) return;
    const da = noten.map(hatWert);
    const vorher = svZahl.current.id === tnId ? svZahl.current.n : da.filter(Boolean).length;
    const jetzt = da.filter(Boolean).length;
    svZahl.current = { id: tnId, n: jetzt };
    if (jetzt <= vorher || ohneBewegung()) return;
    da.forEach((ja, i) => {
      if (ja && i >= vorher) zahlFegt(buehneRef.current, fxRef.current, notenRefs.current[i], punkteText(noten[i].punkte), 2.2, T);
    });
  });

  // Starter blendet ein, wenn ein neuer an der Reihe ist
  const bildRef = useRef(null);
  const nameRef = useRef(null);
  useEffect(() => {
    if (tnId == null || ohneBewegung()) return;
    [bildRef.current, nameRef.current].forEach((e) => animiere(e,
      [{ opacity: 0, transform: 'translateX(-4%)' }, { opacity: 1, transform: 'none' }],
      { duration: 600, easing: 'cubic-bezier(.2,.8,.2,1)' }, T));
  }, [tnId]);

  // ── Rangliste ─────────────────────────────────────────────────────
  // Gezeigt wird, wer fertig ist. Der aktuelle Starter erst, wenn seine
  // Wertung enthüllt ist — sonst stünde sein Platz vor seiner Summe da.
  const roh = Array.isArray(stand?.rangliste) ? stand.rangliste : [];
  const rang = roh
    .filter((r) => {
      const punkte = Number(r.gesamt_punkte) || 0;
      if (r.wettkaempfer_id === tnId) {
        if (sv) return punkte > 0 || r.status === 'completed';
        // Im Bereit-Bild läuft keine Enthüllung mehr: wer schon fertig ist
        // (letzter Starter einer beendeten Kategorie), gehört in die Liste.
        // Vorher fehlte er dort im Endstand (#146, 21.10.2026).
        if (typ === 'bereit' && r.status === 'completed') return true;
        return enth.rang && (punkte > 0 || r.status === 'completed');
      }
      return r.status === 'completed';
    })
    .map((r, i) => ({ ...r, _i: i }))
    .sort((a, b) => ((Number(b.gesamt_punkte) || 0) - (Number(a.gesamt_punkte) || 0)) || (a._i - b._i));
  // Platz bei Gleichstand wie in der Rangliste: gleiche Punkte, gleicher Platz
  const platzVon = (i) => {
    const p = Number(rang[i]?.gesamt_punkte) || 0;
    return rang.findIndex((r) => (Number(r.gesamt_punkte) || 0) === p) + 1;
  };
  const eigenIdx = rang.findIndex((r) => r.wettkaempfer_id === tnId);
  const eigenePunkte = scoring?.gesamt_punkte;
  const platz = stand?.platzierung || (eigenIdx >= 0 ? platzVon(eigenIdx) : null);
  let abstand = null;
  if (eigenIdx >= 0 && rang.length > 1) {
    const eigen = Number(rang[eigenIdx].gesamt_punkte) || 0;
    const ref = eigenIdx === 0 ? (Number(rang[1].gesamt_punkte) || 0) : (Number(rang[0].gesamt_punkte) || 0);
    const d = Math.round((eigen - ref) * 100) / 100;
    abstand = `${d >= 0 ? '+' : '−'}${punkteText(Math.abs(d))}`;
  }

  let zeigen = rang.map((r, i) => [r, i]);
  if (zeigen.length > MAX_ZEILEN) {
    zeigen = zeigen.slice(0, MAX_ZEILEN);
    if (eigenIdx >= MAX_ZEILEN) zeigen[MAX_ZEILEN - 1] = [rang[eigenIdx], eigenIdx];
  }
  let y = 0;
  const zeilen = zeigen.map(([r, i]) => {
    const z = { r, i, top: y, platz: platzVon(i) };
    y += (i === 0 ? 6.8 : 4.6) + (i < 3 ? 0.7 : 0);
    return z;
  });

  // Einsortieren: Zeilen gleiten an ihre neue Stelle, neue fliegen herein
  const altTops = useRef(new Map());
  const zeilenRefs = useRef(new Map());
  const fuehrer = useRef(null);
  const signatur = zeilen.map((z) => `${z.r.wettkaempfer_id}:${z.top.toFixed(1)}:${z.r.gesamt_punkte}`).join('|');
  useLayoutEffect(() => {
    const neu = new Map(zeilen.map((z) => [z.r.wettkaempfer_id, z.top]));
    if (!ohneBewegung() && altTops.current.size + neu.size > 0) {
      zeilen.forEach((z) => {
        const el = zeilenRefs.current.get(z.r.wettkaempfer_id);
        if (!el) return;
        const alt = altTops.current.get(z.r.wettkaempfer_id);
        if (alt === undefined) {
          animiere(el, [{ opacity: 0, transform: 'translateX(-30%) scale(1.1)', background: 'rgba(232,194,90,.5)' }, { opacity: 1, transform: 'none' }],
            { duration: 650, easing: 'cubic-bezier(.2,.8,.2,1)' }, T);
        } else if (Math.abs(alt - z.top) > 0.05) {
          animiere(el, [{ top: `${alt}cqh` }, { top: `${z.top}cqh` }], { duration: 600, easing: 'cubic-bezier(.3,.8,.25,1)' }, T);
        }
      });
      const erster = zeilen[0]?.r.wettkaempfer_id ?? null;
      if (fuehrer.current !== null && erster !== null && erster !== fuehrer.current) {
        animiere(zeilenRefs.current.get(erster), [
          { boxShadow: '0 0 0 0 rgba(255,215,110,.9)' }, { boxShadow: '0 0 2.4cqw .4cqw rgba(255,215,110,.7)' }, { boxShadow: '0 0 0 0 rgba(255,215,110,0)' },
        ], { duration: 1600 }, T);
      }
    }
    fuehrer.current = zeilen[0]?.r.wettkaempfer_id ?? null;
    altTops.current = neu;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatur]);

  // ── Starterliste unten ────────────────────────────────────────────
  const starter = Array.isArray(stand?.starter) ? stand.starter : [];
  // Erster in „folgende“, der nicht gerade selbst dran ist (das Panel schickt
  // gleich nach dem Wechsel mitunter noch den aktuellen Starter mit).
  const naechster = (stand?.folgende || []).find((f) => f.wettkaempfer_id == null || f.wettkaempfer_id !== tnId) || null;
  const bandRef = useRef(null);
  const bandAnim = useRef(null);
  const bandUeber = useRef(-1);
  const bandSig = starter.map((s) => `${s.wettkaempfer_id}:${s.status}`).join('|');
  useEffect(() => {
    // Läuft hin und her, wenn sie breiter ist als ihr Feld (Muster: laufschrift)
    const band = bandRef.current;
    if (!band || !band.parentElement || ohneBewegung()) return;
    const ueber = band.scrollWidth - band.parentElement.clientWidth;
    if (bandAnim.current && Math.abs(ueber - bandUeber.current) < 2) return;
    const stelle = bandAnim.current ? bandAnim.current.currentTime : 0;
    if (bandAnim.current) bandAnim.current.cancel();
    bandAnim.current = null;
    bandUeber.current = ueber;
    if (ueber > 4) {
      const t = Math.max(6000, ueber * 40);
      bandAnim.current = band.animate([
        { transform: 'translateX(0)' }, { transform: 'translateX(0)', offset: 0.1 },
        { transform: `translateX(${-ueber}px)`, offset: 0.5 }, { transform: `translateX(${-ueber}px)`, offset: 0.6 },
        { transform: 'translateX(0)' },
      ], { duration: t * 2, iterations: Infinity, easing: 'ease-in-out' });
      bandAnim.current.currentTime = stelle % (t * 2);
    }
  }, [bandSig]);
  useEffect(() => () => { if (bandAnim.current) bandAnim.current.cancel(); }, []);

  // ── Texte ─────────────────────────────────────────────────────────
  const zeigtNoten = sv || vollstaendig;
  const notenText = noten.map((w) => punkteText(w.punkte));
  const summeText = (sv ? (Number(scoring?.gesamt_punkte) > 0 || vollstaendig) : vollstaendig) ? punkteText(scoring?.gesamt_punkte ?? eigenePunkte) : '–';
  const platzText = platz ? `${platz}.` : '–';
  texte.current = { noten: notenText, summe: summeText, platz: platzText };

  const eingegangen = noten.filter((w) => w.eingegangen).length;
  let regel = '';
  if (typ === 'disqualifikation') regel = '';
  else if (sv) regel = scoring?.unterzeile || '';
  else if (!tn) regel = '';
  else if (!vollstaendig) regel = noten.length ? `${eingegangen} von ${noten.length} Wertungen eingegangen` : '';
  else if (enth.summe) regel = gestrichen.length ? 'Höchste und niedrigste Note gestrichen' : (scoring?.unterzeile || '');

  // „Starter 4 / 7“: Stelle des aktuellen Starters in der Startreihenfolge.
  // Das Panel zählt fertige + 1; nach dem Speichern wäre das schon der Nächste.
  const stelle = starter.findIndex((s) => s.wettkaempfer_id === tnId);
  const starterText = stelle >= 0 ? `${stelle + 1} / ${starter.length}`
    : stand?.fortschritt ? `${stand.fortschritt.position} / ${stand.fortschritt.gesamt}` : '–';
  const timer = stand?.timer;
  const zeitZeigen = !!timer?.aktiv && !enth.summe && typ !== 'ergebnis' && typ !== 'disqualifikation';
  const bild = starterBild(tn, stand?.logo, halle);
  const kopfZeile2 = [stand?.turnier, Number(matte) > 0 ? `Matte ${Number(matte)}` : ''].filter(Boolean).join(' · ');
  const nachName = (p) => name(p);

  // Ankündigung (21.10.2026): nach „Kategorie abschließen“ ruft das Panel die
  // nächste Kategorie auf; bis der erste Starter beginnt, steht sie hier groß.
  const ank = typ === 'bereit' && stand?.ankuendigung ? stand.ankuendigung : null;
  const ankMatte = Number(ank?.matte) > 0 ? Number(ank.matte) : (Number(matte) > 0 ? Number(matte) : null);
  const ankListe = starter.slice(0, 12);
  // Angekündigt wird eine Kategorie anderer Art (Kampf, Bruchtest): Titel
  // ist dann ihre Art, nicht „Kata“.
  const titel = ank?.art || (sv ? 'Selbstverteidigung' : 'Kata');

  return (
    <div className="kt-flaeche">
      <div className="kt" ref={buehneRef} style={{ backgroundImage: bildUrl(`${process.env.PUBLIC_URL || ''}/turniertag/tafel_gleich.jpg`) }}>
        <div className={`kt-x kt-kopf ${sv ? 'sv' : ''}`} style={{ left: '16.5%', top: '6.5%', width: '46%', height: '13%' }}>
          <div className={`kt-titel ${titel.length > 4 ? 'lang' : ''}`}>{titel}</div>
          <div className="kt-kopf-text">
            <div>{stand?.kategorie || ''}</div>
            {kopfZeile2 && <div>{kopfZeile2}</div>}
          </div>
        </div>

        <div className="kt-noten" style={{ gridTemplateColumns: `repeat(${Math.max(1, noten.length)}, 1fr)` }}>
          {noten.map((w, i) => {
            const weg = enth.strich && gestrichen.includes(Number(w.kampfrichter_nummer ?? i + 1));
            const sichtbar = zeigtNoten && (sv || i < enth.n) && hatWert(w);
            return (
              <div key={i} className={`kt-note ${weg ? 'weg' : ''}`}>
                <span className="kt-kr">{sv ? 'Übung' : 'KR'} {w.kampfrichter_nummer ?? i + 1}</span>
                <span className="kt-w" ref={(el) => { notenRefs.current[i] = el; }}>
                  {sichtbar ? notenText[i] : (!zeigtNoten && w.eingegangen ? <i className="kt-punkt" aria-label="eingegangen" /> : '–')}
                </span>
                <span className={`kt-strich ${weg && ohneBewegung() ? 'an' : ''}`} data-weg={weg ? '1' : undefined}
                  ref={(el) => { strichRefs.current[i] = el; }} />
              </div>
            );
          })}
        </div>

        <div className={`kt-x kt-eb ${bild.art}`} ref={bildRef}
          style={{ left: '7.48%', top: '31.99%', width: '9.81%', height: '14.98%', backgroundImage: bildUrl(bild.url) }} />
        <div className="kt-x kt-name" ref={nameRef} style={{ left: '19.38%', top: '32.31%', width: '31.10%', height: '14.35%' }}>
          <div className="kt-nm">{tn ? nachName(tn) : (typ === 'rangliste' ? 'Zwischenstand' : 'Bereit')}</div>
          <div className="kt-vn">{tn?.verein_name || ' '}</div>
          <div className="kt-form">
            {/* Gewählte Form (21.10.2026) statt Startnummer und Gurt, wenn gesetzt */}
            {tn ? (tn.form_name ? `Form: ${tn.form_name}`
              : [tn.startnummer ? `Startnummer ${tn.startnummer}` : '', tn.gurtfarbe || ''].filter(Boolean).join(' · ') || ' ') : ' '}
          </div>
        </div>
        <div className="kt-x kt-zahlfeld" style={{ left: '52.99%', top: '31.99%', width: '8.85%', height: '14.98%' }}>
          <div className="kt-l">Platz</div>
          <div className="kt-z" ref={platzRef}>{(sv || enth.platz) && tn && typ !== 'disqualifikation' ? platzText : '–'}</div>
        </div>
        <div className="kt-x kt-zahlfeld" style={{ left: '7.48%', top: '57.92%', width: '9.75%', height: '14.88%' }}>
          <div className="kt-l">Starter</div>
          <div className="kt-z klein">{starterText}</div>
        </div>
        <div className={`kt-x kt-wertung ${typ === 'disqualifikation' ? 'dq' : ''}`} style={{ left: '19.38%', top: '58.24%', width: '31.10%', height: '14.35%' }}>
          <div className="kt-l">
            {typ === 'disqualifikation' ? 'Disqualifiziert' : 'Wertung'}
            <small>{regel || ' '}</small>
          </div>
          {typ !== 'disqualifikation' && <div className="kt-z" ref={summeRef}>{sv || enth.summe ? summeText : '–'}</div>}
        </div>
        <div className="kt-x kt-zahlfeld" style={{ left: '52.99%', top: '57.92%', width: '8.85%', height: '14.88%' }}>
          {zeitZeigen ? (
            <>
              <div className="kt-l">Zeit</div>
              <div className={`kt-z klein ${timer.sekunden < 30 && timer.gestartet ? 'knapp' : ''}`}>{formatZeit(timer.sekunden)}</div>
            </>
          ) : (
            <>
              <div className="kt-l">Abstand</div>
              <div className="kt-z klein">{(sv || enth.platz) && abstand ? abstand : '–'}</div>
            </>
          )}
        </div>

        <div className={`kt-x kt-rechts ${typ === 'rangliste' ? 'betont' : ''}`} style={{ left: '65.85%', top: '22.95%', width: '26.73%', height: '52.92%' }}>
          <h4>Rangliste</h4>
          <div className="kt-rliste">
            {zeilen.map(({ r, i, top, platz: p }) => (
              <div key={r.wettkaempfer_id ?? i}
                ref={(el) => { if (el) zeilenRefs.current.set(r.wettkaempfer_id, el); else zeilenRefs.current.delete(r.wettkaempfer_id); }}
                className={`kt-rz ${i === 0 ? 'erster' : ''} ${p <= 3 && i < 3 ? `m${p}` : ''} ${r.wettkaempfer_id === tnId ? 'neu' : ''}`}
                style={{ top: `${top}cqh` }}>
                <span className="kt-rp">{p <= 3 && i < 3 ? <Medaille platz={p} /> : `${p}.`}</span>
                <span className="kt-rn">{nachName(r)}<small>{r.verein_name || ''}</small></span>
                <span className="kt-rw">{punkteText(r.gesamt_punkte)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="kt-x kt-vorb">
          <span className="kt-vl">In Vorbereitung</span>
          <span className="kt-vn2">
            {naechster ? <>{nachName(naechster)}{naechster.verein_name && <small>{naechster.verein_name}</small>}</>
              : (starter.length && starter.every((s) => s.status === 'completed' || s.status === 'disqualified') ? 'Kategorie abgeschlossen' : '–')}
          </span>
        </div>
        {starter.length > 0 && (
          <div className="kt-x kt-sliste">
            <span className="kt-sl">Starter</span>
            <div className="kt-sfenster">
              <div className="kt-sband" ref={bandRef}>
                {starter.map((s) => {
                  // Der aktuelle Starter gilt erst nach dem Enthüllen als fertig:
                  // sonst stünde seine Summe unten, bevor sie oben einfegt.
                  const istJetzt = s.wettkaempfer_id === tnId;
                  const fertig = (s.status === 'completed' || s.status === 'disqualified') && (!istJetzt || sv || enth.rang);
                  const art = istJetzt && !fertig ? 'jetzt'
                    : fertig ? 'fertig'
                      : naechster && s.wettkaempfer_id === naechster.wettkaempfer_id ? 'vorb' : '';
                  return (
                    <span key={s.wettkaempfer_id} className={`kt-st ${art}`}>
                      <b>{s.startnummer ?? ''}</b>{nachName(s)}
                      {fertig && s.status === 'completed' && s.gesamt_punkte != null && <i>{punkteText(s.gesamt_punkte)}</i>}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {ank && (
          <Ankuendigung key={`${ank.kategorie}-${ankMatte}`} ank={ank} matte={ankMatte} liste={ankListe}
            mehr={Math.max(0, starter.length - ankListe.length)} gesamt={ank.anzahl || starter.length} nachName={nachName} />
        )}
        <div className="kt-fx" ref={fxRef} />
      </div>
    </div>
  );
}

// Große Ankündigung der aufgerufenen Kategorie über Noten- und Starterfeld.
function Ankuendigung({ ank, matte, liste, mehr, gesamt, nachName }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ohneBewegung() || !ref.current) return;
    animiere(ref.current, [{ opacity: 0, transform: 'scale(.96)' }, { opacity: 1, transform: 'none' }],
      { duration: 700, easing: 'cubic-bezier(.2,.8,.2,1)' }, T);
  }, []);
  const spalten = liste.length > 8 ? 3 : liste.length > 4 ? 2 : 1;
  return (
    <div className="kt-x kt-ank" ref={ref} style={{ left: '6.4%', top: '20.2%', width: '56.4%', height: '57.6%' }}>
      <div className="kt-ank-l">{matte ? `Jetzt auf Matte ${matte}` : 'Jetzt'}</div>
      <div className="kt-ank-name">{ank.kategorie}</div>
      {/* Andere Art (Kampf, Bruchtest) auf derselben Matte: Art dazu */}
      <div className="kt-ank-zahl">{ank.art ? `${ank.art} · ` : ''}{gesamt > 0 ? `${gesamt === 1 ? '1 Starter' : `${gesamt} Starter`} · ` : ''}bitte bereitmachen</div>
      {liste.length > 0 && (
        <div className="kt-ank-liste" style={{ gridTemplateColumns: `repeat(${spalten}, minmax(0, 1fr))` }}>
          {liste.map((s, i) => (
            <div key={s.wettkaempfer_id ?? i} className={`kt-ank-st ${i === 0 ? 'erster' : ''}`}>
              <b>{s.startnummer ?? i + 1}</b><span>{nachName(s)}</span>
            </div>
          ))}
          {mehr > 0 && <div className="kt-ank-st mehr"><span>und {mehr} weitere</span></div>}
        </div>
      )}
    </div>
  );
}

const formatZeit = (sekunden) => {
  const s = Math.max(0, Number(sekunden) || 0);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
